import {FormControl, InputLabel, MenuItem, Select} from '@mui/material';
import {ExamModule} from '@cp/model';
import useArraySetFirstValue from '../../hooks/helper/useArraySetFirstValue';
import useDataExtractorAndValidator from '../../hooks/helper/useDataExtractorAndValidator';

export interface DropdownModuleProps {
  /** The id of the module selected */
  moduleId?: string;
  /** The function to call when the module is changed */
  onChange: (moduleId: string) => void;
  /** The list of exam modules to display in the dropdown */
  examModules: ExamModule[];
  /** If true, the dropdown will be disabled */
  disabled?: boolean;
  /** The label of the dropdown */
  label?: string;
  /** If true, the dropdown will be required */
  required?: boolean;
  /** If true, the first module will be selected if no module is selected */
  autoSelectFirst?: boolean;
  /** If true, the dropdown will be hidden */
  hidden?: boolean;
}

export default function DropdownModule({
  moduleId,
  onChange,
  examModules,
  disabled = false,
  label = 'Module',
  required = false,
  autoSelectFirst = false,
  hidden = false,
}: DropdownModuleProps) {
  const {data: moduleIds, selected: validatedModuleId} = useDataExtractorAndValidator<ExamModule, string>(
    examModules,
    moduleId,
    (module) => module.id,
    (moduleId, selectedModuleId) => moduleId === selectedModuleId,
    '',
  );

  useArraySetFirstValue(moduleIds, validatedModuleId, (moduleId) => onChange(moduleId || ''), autoSelectFirst);

  if (hidden) return null;

  return (
    <FormControl fullWidth data-testid="dropdown-module">
      <InputLabel>
        {label}
        {required ? ' *' : ''}
      </InputLabel>
      <Select
        value={validatedModuleId}
        onChange={(e) => onChange(e.target.value)}
        label={label + (required ? ' *' : '')}
        disabled={disabled || examModules.length === 0}
      >
        {examModules.map((module) => (
          <MenuItem key={module.id} value={module.id}>
            {module.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
